import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  SpeedDial,
  SpeedDialAction,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  EmojiEvents as TrophyIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { getAllRoutes, getDifficultyColor } from './routeLogic';

const GameControls = ({ onNewRoute }) => {
  const [showRules, setShowRules] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const routes = getAllRoutes();

  const handleSpeedDialToggle = () => {
    setSpeedDialOpen(!speedDialOpen);
  };

  const handleSpeedDialClose = () => {
    setSpeedDialOpen(false);
  };

  // Shared Dialogs Component
  const dialogs = (
    <>
      {/* Traffic Rules Dialog */}
      <Dialog
        open={showRules}
        onClose={() => setShowRules(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#2a2a2a', color: '#fff' }}>
          Model Town Board - Traffic Rules
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1a1a1a', color: '#fff', mt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#4caf50' }}>
            Roundabout Rules:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Move clockwise (keep left) in East Africa" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Give way to traffic already on the roundabout" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Signal right when approaching your turn" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Signal left when exiting" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• No stopping, parking, or overtaking on the roundabout" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Stay in your lane - do not change lanes" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2, bgcolor: '#333' }} />

          <Typography variant="h6" gutterBottom sx={{ color: '#4caf50' }}>
            General Road Rules:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Keep left - drive on the left side of the road" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• White lines - separate lanes going same direction" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Yellow lines - separate opposite traffic directions" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Do not cross yellow continuous lines" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Stay in your lane - maintain lane discipline" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Yellow kerbs - No stopping, waiting, or parking" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2, bgcolor: '#333' }} />

          <Typography variant="h6" gutterBottom sx={{ color: '#4caf50' }}>
            U-Turn Rules:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Only make U-turns at designated areas" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Check mirrors before executing" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Signal your intention" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Ensure safe gap in traffic" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2, bgcolor: '#333' }} />

          <Typography variant="h6" gutterBottom sx={{ color: '#4caf50' }}>
            Parking Rules:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Angle parking: Park at 45° angle" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Follow parking area direction arrows" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Park within marked slots" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Observe entry and exit points" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#2a2a2a' }}>
          <Button onClick={() => setShowRules(false)} sx={{ color: '#4caf50' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Available Routes Dialog */}
      <Dialog
        open={showRoutes}
        onClose={() => setShowRoutes(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#2a2a2a', color: '#fff' }}>
          Available Practice Routes
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1a1a1a', color: '#fff', mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#aaa' }}>
            The system will randomly select one of these routes for you to practice:
          </Typography>

          {routes.map((route, index) => (
            <Card
              key={route.id}
              sx={{
                mb: 2,
                bgcolor: '#2a2a2a',
                border: '1px solid #333'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ color: '#fff' }}>
                    {route.name}
                  </Typography>
                  <Chip
                    label={route.difficulty.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: getDifficultyColor(route.difficulty),
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                  {route.description}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {route.checkpoints.length} checkpoints
                </Typography>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#2a2a2a' }}>
          <Button onClick={() => setShowRoutes(false)} sx={{ color: '#2196f3' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (isMobile) {
    // Mobile: Floating Action Button with Speed Dial
    return (
      <>
        <SpeedDial
          ariaLabel="Game Controls"
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 16,
            '& .MuiSpeedDial-fab': {
              bgcolor: '#2196f3',
              '&:hover': {
                bgcolor: '#1976d2'
              }
            }
          }}
          icon={<MenuIcon />}
          onClose={handleSpeedDialClose}
          onOpen={handleSpeedDialToggle}
          open={speedDialOpen}
        >
          <SpeedDialAction
            icon={<RefreshIcon />}
            tooltipTitle="New Route"
            onClick={() => {
              onNewRoute();
              handleSpeedDialClose();
            }}
            sx={{
              bgcolor: '#fff',
              color: '#ff9800',
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
          />
          <SpeedDialAction
            icon={<InfoIcon />}
            tooltipTitle="Traffic Rules"
            onClick={() => {
              setShowRules(true);
              handleSpeedDialClose();
            }}
            sx={{
              bgcolor: '#fff',
              color: '#4caf50',
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
          />
          <SpeedDialAction
            icon={<TrophyIcon />}
            tooltipTitle="Available Routes"
            onClick={() => {
              setShowRoutes(true);
              handleSpeedDialClose();
            }}
            sx={{
              bgcolor: '#fff',
              color: '#2196f3',
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
          />
        </SpeedDial>
        {dialogs}
      </>
    );
  }

  // Desktop: Traditional button layout
  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={() => setShowRules(true)}
          sx={{ borderColor: '#4caf50', color: '#4caf50' }}
        >
          Traffic Rules
        </Button>

        <Button
          variant="outlined"
          startIcon={<TrophyIcon />}
          onClick={() => setShowRoutes(true)}
          sx={{ borderColor: '#2196f3', color: '#2196f3' }}
        >
          Available Routes
        </Button>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={onNewRoute}
          sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
        >
          New Route
        </Button>
      </Box>
      {dialogs}
    </Box>
  );
};

export default GameControls;
